import { useCallback, useEffect, useState } from 'react'

import { Activity, CustomListForm, Renew } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUnit } from 'effector-react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { FormController } from '@/components/ui/FormController'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { TextField } from '@/components/ui/text-field'
import { $currentCreator } from '@/models/creators.ts'
import { CustomListFormSchema } from '@/modules/creator-settings/lists/custom-lists/types'
import type { Creator } from '@/modules/creators/types'

import type { DialogProps } from '../types'
import {
  CustomListFormField,
  CustomListsFormField,
  type CustomListsFormFieldConfig,
} from './CustomListsFormField'

interface Props extends DialogProps {
  onSubmit?: (values: CustomListForm) => Promise<unknown | undefined>
  title: string
  initialValues?: CustomListForm
  submitLabel?: string
  viewOnly?: boolean
  testId?: string
}

const activityOptions = [
  { label: 'All', value: Activity.ALL },
  { label: 'Active', value: Activity.ACTIVE },
  { label: 'Expired', value: Activity.EXPIRED },
]

const MAX_LISTS_TO_SELECT = 10
const formFields = (activeCreator: Creator): CustomListsFormFieldConfig[] => {
  const activeCreatorOptions = activeCreator.lists.map((list) => ({
    label: list.name,
    value: list.id,
  }))

  return [
    {
      name: 'activity',
      label: 'Necessary selection',
      type: 'single-select',
      options: activityOptions,
      defaultValue: Activity.ALL,
    },
    {
      name: 'amountSpent',
      label: 'Minimum Amount Spent',
      type: 'number',
      suffix: 'USD',
      min: 1,
      defaultValue: 1,
    },
    {
      name: 'spendMoneyLastNDays',
      label: 'Spend money in last X days',
      type: 'number',
      suffix: 'Days',
      min: 1,
    },
    {
      name: 'timeSubscribedDays',
      label: 'Time subscribed in days',
      type: 'number',
      suffix: 'Days',
      min: 1,
    },
    {
      name: 'listIds',
      label: 'In existing lists already',
      placeholder: 'No lists selected',
      hint: 'You can select a maximum of 10 lists.',
      type: 'multi-select',
      maxSelection: MAX_LISTS_TO_SELECT,
      options: activeCreatorOptions,
      defaultValue: [],
      entityName: 'Lists',
    },
    {
      name: 'atLeastRespondedOnceInLastNDays',
      label: 'Replied in last X days',
      type: 'number',
      suffix: 'Days',
      min: 1,
    },
    {
      name: 'renew',
      label: 'Renew',
      type: 'single-select',
      options: [
        { label: 'Both', value: Renew.BOTH },
        { label: 'On', value: Renew.ON },
        { label: 'Off', value: Renew.OFF },
      ],
      defaultValue: Renew.BOTH,
      entityName: 'Renew',
    },
    {
      name: 'amountOfTips',
      label: 'Minimum amount of tips',
      type: 'number',
      suffix: 'USD',
      min: 1,
    },
    {
      name: 'countOfPpvBought',
      label: 'Minimum amount of PPV’s bought',
      type: 'number',
      min: 1,
    },
  ]
}

export const CustomListsFormDialog = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  initialValues,
  submitLabel = 'Create',
  viewOnly = false,
  testId,
}: Props) => {
  const activeCreator = useUnit($currentCreator)!
  const [enabledFields, setEnabledFields] = useState<
    Record<CustomListFormField, boolean>
  >({
    activity: true,
    amountSpent: false,
    spendMoneyLastNDays: false,
    timeSubscribedDays: false,
    listIds: false,
    atLeastRespondedOnceInLastNDays: false,
    renew: false,
    amountOfTips: false,
    countOfPpvBought: false,
  })

  // Check if any field other than 'name' is enabled to determine if submit button should be active
  const hasAnyFieldSelected = Object.values(enabledFields).some(Boolean)

  const form = useForm<CustomListForm>({
    resolver: zodResolver(CustomListFormSchema),
    defaultValues: {
      ...initialValues,
      activity: initialValues?.activity || Activity.ALL,
      renew: initialValues?.renew || Renew.BOTH,
    },
    mode: 'onChange',
  })

  const {
    handleSubmit,
    formState: { isValid },
  } = form

  const toggleField = useCallback(
    (fieldName: CustomListFormField) => {
      setEnabledFields((prev) => {
        const newState = { ...prev, [fieldName]: !prev[fieldName] }

        if (!newState[fieldName]) {
          form.clearErrors(fieldName)
        }

        return newState
      })
    },
    [form],
  )

  useEffect(() => {
    if (initialValues) {
      const activeFields = Object.entries(initialValues || {}).reduce<
        Record<string, boolean>
      >(
        (acc, [key, value]) => {
          if (
            value !== undefined &&
            value !== null &&
            (!Array.isArray(value) || value.length > 0)
          ) {
            acc[key] = true
          }

          return acc
        },
        {} as Record<string, boolean>,
      )

      setEnabledFields(activeFields)
    }
  }, [initialValues])

  const handleFormSubmit = useCallback(
    async (values: CustomListForm) => {
      const filteredValues = Object.entries(values).reduce(
        (acc, [key, value]) => {
          const field = key as CustomListFormField

          if (enabledFields[field]) {
            acc[key] = value
          }

          return acc
        },
        {
          name: values.name,
        } as Record<string, string | number | boolean | string[]>,
      )

      await onSubmit?.({ ...filteredValues, name: values.name })

      onClose()
    },
    [onSubmit, onClose, enabledFields],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      panelClassName="w-[400px]"
      testId={testId}
    >
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Dialog.Header>
            <Dialog.Title>{title}</Dialog.Title>
          </Dialog.Header>
          <Dialog.Description>
            <div className="flex flex-col gap-6 pt-6">
              <Controller
                name="name"
                render={({ field, fieldState }) => (
                  <FormController>
                    <FormController.Label htmlFor="list-name">
                      List Name
                    </FormController.Label>
                    <FormController.Input>
                      <TextField
                        {...field}
                        onChange={field.onChange}
                        id="list-name"
                        placeholder="Enter list name"
                        disabled={viewOnly}
                        testId="name-input"
                      />
                    </FormController.Input>
                    {fieldState.error?.message && (
                      <FormController.Error>
                        {fieldState.error?.message}
                      </FormController.Error>
                    )}
                  </FormController>
                )}
              />
              {formFields(activeCreator).map((fieldConfig) => (
                <CustomListsFormField
                  key={fieldConfig.name}
                  field={fieldConfig}
                  enabled={enabledFields[fieldConfig.name]}
                  onToggle={() => toggleField(fieldConfig.name)}
                  viewOnly={viewOnly}
                />
              ))}
            </div>
          </Dialog.Description>
          <Dialog.Footer>
            <Button
              type="button"
              onClick={onClose}
              className="flex-1"
              variant="outline"
              testId="dialog-close-button"
            >
              {viewOnly ? 'Close' : 'Cancel'}
            </Button>
            {!viewOnly && (
              <Button
                isLoading={form.formState.isSubmitting}
                disabled={!hasAnyFieldSelected || !isValid}
                className="flex-1"
                testId="dialog-submit-button"
              >
                {submitLabel}
              </Button>
            )}
          </Dialog.Footer>
        </form>
      </FormProvider>
    </Dialog>
  )
}
